import React, { useState, useCallback, useMemo, useContext } from "react";
import {
  PageHeader,
  Icons,
  IconButton,
  Button,
  Text,
  useClientTable,
  DataTable,
  Menu,
} from "@pushpress/shared-components";
import { useToggle } from "../../../hooks/state";
import { openWindow } from "../../../utils/browserUtils";
import {
  AppointmentDiscount,
  DiscountType,
} from "../../../apis/appointmentApi";
import { useAppointmentDiscountsQuery } from "../../../apis/appointmentApi.hooks";
import PageContainer from "../../../components/common/PageContainer/PageContainer";
import { AppContext } from "../../../context/app";
import { formatToPercent, formatToUSD } from "../../../utils/misc";
import DiscountDrawer from "../DiscountDrawer/DiscountDrawer";
import DeleteModal from "./AppointmentDiscountDeleteModal";
import { EmptyState } from "@pushpress/pushpress-ui";
import DiscountIcon from "../../../components/icons/DiscountIcon";

function AppointmentDiscountList() {
  const app = useContext(AppContext);

  const [editData, setEditData] = useState<DiscountType>();
  const [drawerVisible, drawerActions] = useToggle(false);
  const [duplicate, duplicateActions] = useToggle(false);
  const [deleteVisible, deleteActions] = useToggle(false);

  const discountsQuery = useAppointmentDiscountsQuery();

  const columns = useMemo(
    () => [
      {
        key: "name",
        header: "Name",
        width: "400px",
        widthMobile: "auto",
        isSortable: true,
      },
      {
        key: "discountValue",
        header: "Amount",
        hideOnMobile: true,
        cell: (row: AppointmentDiscount) => {
          const value =
            row.discountType === "PERCENTAGE"
              ? formatToPercent(row.discountValue)
              : formatToUSD(row.discountValue, app.client.currencyIso);
          return <Text variant="body-lg">{value}</Text>;
        },
      },
    ],
    [app.client.currencyIso],
  );

  const { sort, data, handleSort } = useClientTable({
    data: discountsQuery?.data?.data || [],
    columns,
    defaultSort: { key: "name", order: "asc" },
  });

  const handleEditClick = (data: AppointmentDiscount) => {
    setEditData(data);
    duplicateActions.off();
    drawerActions.on();
  };

  const handleDuplicateClick = (data: AppointmentDiscount) => {
    setEditData(data);
    duplicateActions.on();
    drawerActions.on();
  };

  const handleDeleteClick = (data: AppointmentDiscount) => {
    setEditData(data);
    deleteActions.on();
  };

  const handleAdd = useCallback(() => {
    setEditData(undefined);
    duplicateActions.off();
    drawerActions.on();
  }, [duplicateActions, drawerActions]);

  const handleSuccess = () => {
    drawerActions.off();
    discountsQuery.refetch();
  };

  const handleHelp = () => {
    openWindow(
      "https://help.pushpress.com/en/articles/6310433-appointments-beta-system-overview",
    );
  };

  const getRowId = (row: AppointmentDiscount) => {
    return row.uuid;
  };

  const renderRowMenu = (row: AppointmentDiscount, closeMenu: VoidFunction) => {
    return (
      <Menu>
        <Menu.Item
          label="Edit"
          action={() => {
            handleEditClick(row);
            closeMenu();
          }}
        ></Menu.Item>
        <Menu.Item
          label="Duplicate"
          action={() => {
            handleDuplicateClick(row);
            closeMenu();
          }}
        ></Menu.Item>
        <Menu.Item
          label="Delete"
          action={() => {
            handleDeleteClick(row);
            closeMenu();
          }}
        ></Menu.Item>
      </Menu>
    );
  };

  const emptyState = (
    <EmptyState
      title="No discounts found"
      icon={<DiscountIcon />}
      description="Create a discount to offer your clients a percentage or dollar amount off."
      primaryActionLabel="Add Discount"
      primaryActionVariant="dark"
      primaryActionOnClick={handleAdd}
    />
  );

  return (
    <div className="tw-flex tw-flex-col md:tw-px-8 md:tw-pt-4 tw-w-full tw-mx-auto tw-max-w-7xl">
      <PageContainer>
        <PageHeader title="Appointment Discounts">
          <PageHeader.ActionMenu>
            <IconButton
              icon={Icons.Help}
              tooltip="Learn more"
              onClick={handleHelp}
            />
            <Button
              text="Discount"
              size="large"
              iconLocation="left"
              hideTextOnMobile
              icon={Icons.Plus}
              iconBold
              onClick={handleAdd}
            />
          </PageHeader.ActionMenu>
        </PageHeader>
        <DataTable
          data={data}
          columns={columns}
          sort={sort}
          loading={discountsQuery.isLoading}
          getRowId={getRowId}
          onSort={handleSort}
          renderRowMenu={renderRowMenu}
          customEmptyState={emptyState}
        />
      </PageContainer>
      <DiscountDrawer
        visible={drawerVisible}
        duplicate={duplicate}
        onClose={drawerActions.off}
        onSuccess={handleSuccess}
        appointmentDiscountUuid={editData?.uuid}
      />
      <DeleteModal
        visible={deleteVisible}
        appointmentDiscountUuid={editData?.uuid}
        onClose={deleteActions.off}
        onSuccess={handleSuccess}
      />
    </div>
  );
}

export default AppointmentDiscountList;
